import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, ModalComponent, ModalFooterDirective } from 'mbs-ui-kit';
import { AwsDetail, Balance, Billing, WasabiDetail } from 'mbs-ui-shared@components/components/models/license.model';
import { LicensesPipesModule } from 'mbs-ui-shared@components/licenses/pipes/licenses-pipes.module';
import { BALANCE_TOKEN } from 'mbs-ui-shared@components/licenses/tokens/balance.token';
import { getBillingPeriods } from 'mbs-ui-shared@components/licenses/utils/billing-periods.util';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LicenseMode } from 'mbs-ui-shared@models/ProviderData';
import { UnifiedBillingAccountType } from 'mbs-ui-shared@models/storage-accounts/UnifiedBillingAccount';
import { LicensesService } from 'mbs-ui-shared@services/licenses.service';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { catchError, finalize, Observable, of } from 'rxjs';
import { AwsTableComponent } from '../aws-table/aws-table.component';
import { RenewalTableComponent } from '../renewal-table/renewal-table.component';
import { WasabiTableComponent } from '../wasabi-table/wasabi-table.component';

@UntilDestroy()
@Component({
  selector: 'mbs-renew-license-modal',
  templateUrl: './renew-license-modal.component.html',
  styleUrls: ['./renew-license-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RenewalTableComponent,
    WasabiTableComponent,
    AwsTableComponent,
    LicensesPipesModule,
    NgbTooltipModule,
    ButtonComponent,
    I18NextPipe,
    ModalComponent,
    ModalFooterDirective
  ]
})
export class RenewLicenseModalComponent implements OnInit {
  private storageAccountsService = inject(StorageAccountsService);
  private licensesService = inject(LicensesService);
  private auth = inject(AuthFacade);
  balance = inject<Observable<Balance>>(BALANCE_TOKEN);

  protected loading = signal(true);
  protected renewLoading = signal(false);

  protected baseModal = viewChild(ModalComponent);

  protected totalUbWithDebt = signal(0);
  protected licensesInfo = signal<Billing>(null);
  protected awsStoragesInfo = signal<Array<AwsDetail>>(null);
  protected wasabiStoragesInfo = signal<Array<WasabiDetail>>(null);
  protected balanceValue = signal<number>(null);
  private totalPrice = signal<number>(0);
  protected discountedPrice = computed(() => this.licensesInfo()?.proratedPrice - this.licensesInfo()?.discount || 0);
  protected isPaymentLessThanOneDollar = computed(() => this.totalPrice() <= 1);
  protected isNotEnoughCreditsToPay = computed(() => this.totalPrice() > this.balanceValue());
  protected total = computed<number>(() => (this.isNotEnoughCreditsToPay() ? this.totalPrice() - this.balanceValue() : 0));
  protected showMinCommitment = computed(() => Number(this.total()) <= 30);
  protected minCommitment = computed(() => this.baseModal().data?.minCommitment);
  protected awsStoragesTotalCost = computed(() => this.awsStoragesInfo()?.reduce((acc, storage) => acc + storage.totalCost, 0) || 0);
  protected wasabiStoragesTotalCost = computed(() => this.wasabiStoragesInfo()?.reduce((acc, storage) => acc + storage.total, 0) || 0);
  protected storagesTotalCost = computed(() => this.awsStoragesTotalCost() + this.wasabiStoragesTotalCost());
  protected totalAmount = computed(() => this.storagesTotalCost() + this.discountedPrice());
  protected totalLicenses = computed(() => this.licensesInfo()?.licenses?.reduce((acc, license) => acc + license.quantity, 0));
  protected subscriptionTypeKey = computed(() =>
    this.isYearlyLicenseMode ? 'shared:licenses.annualSubscription' : 'shared:licenses.monthlySubscription'
  );
  protected paymentPeriodKey = computed(() =>
    this.isYearlyLicenseMode ? 'shared:licenses.renewLicenseModal.yearly' : 'shared:licenses.renewLicenseModal.monthly'
  );
  protected licensesBillingPeriods = computed(() =>
    getBillingPeriods(
      (this.licensesInfo()?.licenses ?? []).map(({ billingPeriodStart, billingPeriodEnd }) => ({
        start: billingPeriodStart,
        end: billingPeriodEnd
      })),
      this.isYearlyLicenseMode
    )
  );
  protected storageBillingPeriods = computed(() =>
    getBillingPeriods(
      [...(this.awsStoragesInfo() ?? []), ...(this.wasabiStoragesInfo() ?? [])].map(({ periodStart, periodEnd }) => ({
        start: periodStart,
        end: periodEnd
      })),
      this.isYearlyLicenseMode
    )
  );

  protected get isYearlyLicenseMode(): boolean {
    const licenseMode = this.auth.currentUserValue.ProviderInfo.LicenseMode;
    return (
      licenseMode === LicenseMode.Yearly ||
      licenseMode === LicenseMode.CumulativeV2Annual ||
      licenseMode === LicenseMode.CumulativeV3Annual ||
      licenseMode === LicenseMode.CumulativeV4Annual
    );
  }

  ngOnInit(): void {
    this.storageAccountsService.unifiedBillings$.pipe(untilDestroyed(this)).subscribe({
      next: (data) => {
        this.totalUbWithDebt.set(0);
        data?.items?.forEach((item) => {
          if (item.usageTypeByLifeCycle === UnifiedBillingAccountType.PaidWithDebt) {
            this.totalUbWithDebt.update((value) => value + 1);
          }
        });
      }
    });
    this.licensesService
      .getRenew(this.baseModal().data?.params)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((billing) => {
        this.balanceValue.set(billing.credits);
        this.totalPrice.set(billing.total);
        this.licensesInfo.set(billing?.licensesInfo);
        this.awsStoragesInfo.set(billing?.storagesInfo?.details);
        this.wasabiStoragesInfo.set(billing?.storagesInfo?.wasabiDetails);
      });
  }

  protected renew() {
    this.renewLoading.set(true);

    this.licensesService
      .renew(this.baseModal().data?.params)
      .pipe(
        finalize(() => this.renewLoading.set(false)),
        catchError(() => of(null))
      )
      .subscribe((response) => {
        if (response === null) {
          return;
        }

        const url = response;
        const isEnoughCreditsToPay = url === '';

        if (isEnoughCreditsToPay) {
          this.baseModal().save();
          return;
        }

        location.href = String(url).replace('~', '').replaceAll('"', '');
      });
  }
}
